import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
  FormControl,
  AbstractControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { SearchBarService } from '../../services/search.service';
import { DataService } from '../../../../shared/data/data.service';
import { City, HotelSearchRequest } from '../../../../shared/models/search-bar';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BsDaterangepickerDirective,
  BsDaterangepickerConfig,
} from 'ngx-bootstrap/datepicker';
import { GeolocationService } from '../../../../shared/services/geolocation.service';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-hotel-search',
  standalone: false,
  templateUrl: './hotel-search.component.html',
  styleUrl: './hotel-search.component.scss',
})
export class HotelSearchComponent implements OnInit, OnDestroy {
  @ViewChild('dateRangePicker', { static: false })
  dateRangePicker!: BsDaterangepickerDirective;

  isLoading: boolean = false;

  // Range picker configuration with mobile optimization
  bsRangeConfig: Partial<BsDaterangepickerConfig> = {
    dateInputFormat: 'DD/MM/YYYY',
    containerClass: 'theme-default mobile-responsive',
    showWeekNumbers: false,
    adaptivePosition: false,
    isAnimated: true,
    rangeInputFormat: 'DD/MM/YYYY',
  };

  @Output() search: EventEmitter<string> = new EventEmitter<string>();
  bsValue = this.getToday();
  hotelForm!: FormGroup;
  private _autoSaveSub?: Subscription;
  private _suppressAutoSave: boolean = false;
  // Controls mobile travellers overlay
  mobileTravellersOpen: boolean = false;

  private _bodyScrollLock?: {
    scrollY: number;
    position: string;
    top: string;
    left: string;
    right: string;
    width: string;
    overflow: string;
  };

  private lockBodyScroll(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (this._bodyScrollLock) return;

    const body = document.body;
    this._bodyScrollLock = {
      scrollY: window.scrollY || window.pageYOffset || 0,
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      right: body.style.right,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.classList.add('travellers-open');
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${this._bodyScrollLock.scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
  }

  private unlockBodyScroll(): void {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    if (!this._bodyScrollLock) return;

    const body = document.body;
    const { scrollY, position, top, left, right, width, overflow } = this._bodyScrollLock;

    body.style.position = position;
    body.style.top = top;
    body.style.left = left;
    body.style.right = right;
    body.style.width = width;
    body.style.overflow = overflow;
    body.classList.remove('travellers-open');

    this._bodyScrollLock = undefined;
    window.scrollTo(0, scrollY);
  }

  // Toggle guests panel: on mobile show overlay, otherwise let Bootstrap dropdown work
  onTravellerToggle(event?: Event): void {
    try {
      if (window.innerWidth <= 767) {
        // Prevent Bootstrap dropdown on mobile
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        this.mobileTravellersOpen = true;
        this.lockBodyScroll();
      }
      // Desktop: do nothing extra, Bootstrap handles dropdown open/close
    } catch (e) {
      console.warn('Failed to toggle hotel travellers overlay', e);
    }
  }

  openMobileTravellers(): void {
    this.mobileTravellersOpen = true;
    this.lockBodyScroll();
  }

  closeMobileTravellers(): void {
    this.mobileTravellersOpen = false;
    this.unlockBodyScroll();
  }
  private readonly allowedHotelSearchURLs = [
    'home',
    'hotel-list',
    'hotel-details',
  ];

  constructor(
    private readonly _fb: FormBuilder,
    private readonly searchBarService: SearchBarService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly geo: GeolocationService,
    private readonly dataService: DataService,
    private readonly _cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.searchBarService.isLoading$.subscribe((loading: boolean) => {
      this.isLoading = loading;
    });

    this.hotelForm = new FormGroup(
      {
        customCityId: new FormControl<string | null>(null, {
          validators: Validators.required,
        }),
        city_name: new FormControl<string | null>(null, {
          validators: Validators.required,
        }),
        country_name: new FormControl<string | null>(null, {
          validators: Validators.required,
        }),
        requiredCurrency: new FormControl<string>('USD', {
          validators: Validators.required,
        }),

        checkin: new FormControl<Date | null>(this.getToday(), {
          validators: Validators.required,
        }),
        checkout: new FormControl<Date | null>(
          this.getNextDate(this.getToday(), 1),
          { validators: Validators.required },
        ),
        resultsPerPage: new FormControl<number>(30, {
          validators: [
            Validators.required,
            Validators.min(1),
            Validators.max(100),
          ],
        }),
        maxResult: new FormControl<number>(30, {
          validators: [Validators.required, Validators.min(1)],
        }),
        roomAndGuest: this.createRoomAndGuestFormGroup(),
        occupancy: this._fb.array([]),
        language: new FormControl<string>('en', {
          validators: Validators.required,
        }),
      },
      { validators: this.checkinCheckoutValidator },
    );

    this._suppressAutoSave = true;

    // Patch from query params OR service cache OR localStorage
    this.route.queryParams.subscribe((params) => {
      let formValue = this.searchBarService.fromQueryParams(params);
      formValue = this.normalizeFormValue(formValue);

      if (Object.keys(params).length === 0) {
        let localValue = this.searchBarService.hotelSearchRequest as any;
        if (!localValue) {
          const raw = localStorage.getItem('hotelSearchRequest');
          if (raw) {
            try {
              localValue = JSON.parse(raw) as HotelSearchRequest;
              this.searchBarService.changeHotelSearchRequest(
                localValue as HotelSearchRequest,
              );
            } catch (e) {
              console.warn('Invalid hotelSearchRequest in localStorage', e);
              localValue = null;
            }
          }
        }

        if (!localValue) {
          this._suppressAutoSave = false;
          return;
        }

        if (this.router.url.includes('hotel-list')) {
          formValue = this.searchBarService.fromQueryParams(localValue);

          formValue = this.normalizeFormValue(formValue);

          const querystring = this.searchBarService.toQueryString(formValue);

          this.router.navigateByUrl('/hotel/hotel-list?' + querystring);

          return;
        }
        formValue = this.searchBarService.fromQueryParams(localValue);
        formValue = this.normalizeFormValue(formValue);
      }

      // Patch the main form
      if (formValue) {
        this.hotelForm.patchValue(formValue);

        // occupancy
        if (formValue.occupancy) {
          const arr = this.hotelForm.get('occupancy') as FormArray;
          arr.clear();
          formValue.occupancy.forEach((od: any) => {
            const roomGroup = this._fb.group({
              room_no: [od.room_no, Validators.required],
              adult: [od.adult, [Validators.required, Validators.min(1)]],
              child: [od.child, [Validators.min(0)]],
              child_age: this._fb.array([]),
            });
            if (od.child_age && Array.isArray(od.child_age)) {
              const childAgeArray = roomGroup.get('child_age') as FormArray;
              od.child_age.forEach((age: number) => {
                const clamped = Math.max(1, Math.min(12, Number(age) || 1));
                childAgeArray.push(
                  this._fb.control(clamped, [
                    Validators.required,
                    Validators.min(1),
                    Validators.max(12),
                  ]),
                );
              });
            }
            arr.push(roomGroup);
          });
        }

        // roomAndGuest
        if (formValue.roomAndGuest) {
          const roomAndGuest = this.hotelForm.get('roomAndGuest') as FormGroup;
          roomAndGuest.patchValue({
            roomCount: Number(formValue.roomAndGuest.roomCount) || 1,
            adultCount: Number(formValue.roomAndGuest.adultCount) || 1,
            childCount: Number(formValue.roomAndGuest.childCount) || 0,
          });
        }

        this.searchBarService.changeHotelSearchRequest(
          formValue as HotelSearchRequest,
        );
        if (this.isAllowedHotelSearchUrl)
          this.searchBarService.getHotelSearchData();
        this._cdr.detectChanges();
      }

      this._suppressAutoSave = false;
    });

    this._autoSaveSub = this.hotelForm.valueChanges
      .pipe(debounceTime(400))
      .subscribe((val) => {
        if (this._suppressAutoSave) return;
        try {
          const snapshot = JSON.parse(JSON.stringify(val));
          if (snapshot) {
            const payload = {
              ...snapshot,
              checkin: this.formatLocalDate(snapshot.checkin),
              checkout: this.formatLocalDate(snapshot.checkout),
            };

            localStorage.setItem('hotelSearchRequest', JSON.stringify(payload));
            this.searchBarService.changeHotelSearchRequest(
              payload as HotelSearchRequest,
            );
          }
        } catch (e) {
          console.warn('Failed to autosave hotel form', e);
        }
      });

    // Subscribe to checkin changes and update checkout if needed
    this.hotelForm.get('checkin')?.valueChanges.subscribe((checkinDate) => {
      const checkoutCtrl = this.hotelForm.get('checkout');
      const checkoutDate = checkoutCtrl?.value;
      if (checkinDate && checkoutCtrl) {
        const checkin = new Date(checkinDate);
        const checkout = checkoutDate ? new Date(checkoutDate) : new Date(0);
        if (!checkoutDate || checkout <= checkin) {
          const newCheckout = new Date(checkin);
          newCheckout.setDate(checkin.getDate() + 1);
          checkoutCtrl.setValue(newCheckout);
        }
      }
    });
  }

  private prefillNearestCityIfEmpty(): void {
    const hasCity = !!this.hotelForm?.get('customCityId')?.value;
    if (hasCity) return;
    this.geo.getCoords().subscribe((coords) => {
      if (!coords) return;
      this.dataService
        .getNearestCity(coords.lat, coords.lon)
        .subscribe((city) => {
          if (!city) return;
          this.hotelForm.patchValue({
            customCityId: city.id,
            city_name: city.cityName,
            country_name: city.countryName,
          });
        });
    });
  }

  useNearestCity(): void {
    this.geo.getCoords().subscribe((coords) => {
      if (!coords) return;
      this.dataService
        .getNearestCity(coords.lat, coords.lon)
        .subscribe((city) => {
          if (!city) return;
          this.hotelForm.patchValue({
            customCityId: city.id,
            city_name: city.cityName,
            country_name: city.countryName,
          });
        });
    });
  }
  private getLocalStorageValues() {
    const selectedCountryInfo = localStorage.getItem('selectedCountryInfo');
    if (selectedCountryInfo) {
      const info = this.safeParseJSON<any>(selectedCountryInfo);
      if (info) {
        if (info.currency) {
          this.hotelForm.get('requiredCurrency')?.setValue(info.currency);
        }
        if (info.hotelLanguage) {
          this.hotelForm.get('language')?.setValue(info.hotelLanguage);
        }
      }
    }
  }

  private safeParseJSON<T = any>(text: string): T | null {
    try {
      return JSON.parse(text) as T;
    } catch {
      return null;
    }
  }

  private normalizeFormValue(value: any): any {
    if (!value || typeof value !== 'object') return value;

    const v = { ...value };
    const today = this.getToday();

    // ---- DATE NORMALIZATION ----
    if (v.checkin) {
      v.checkin = new Date(v.checkin);
      if (isNaN(v.checkin.getTime()) || v.checkin < today) {
        v.checkin = today;
      }
    } else {
      v.checkin = today;
    }

    if (v.checkout) {
      v.checkout = new Date(v.checkout);
    }

    if (!v.checkout || v.checkout <= v.checkin) {
      v.checkout = this.getNextDate(v.checkin, 1);
    }

    // ---- NUMERIC FIELDS ----
    if (v.resultsPerPage !== undefined)
      v.resultsPerPage = Number(v.resultsPerPage) || 30;

    if (v.maxResult !== undefined) v.maxResult = Number(v.maxResult) || 30;

    // ---- ROOM & GUEST ----
    if (v.roomAndGuest && typeof v.roomAndGuest === 'object') {
      v.roomAndGuest = {
        roomCount: Number(v.roomAndGuest.roomCount) || 1,
        adultCount: Number(v.roomAndGuest.adultCount) || 1,
        childCount: Number(v.roomAndGuest.childCount) || 0,
        child_age: Array.isArray(v.roomAndGuest.child_age)
          ? v.roomAndGuest.child_age.map((a: any) => Number(a) || 1)
          : [],
      };
    }

    // ---- OCCUPANCY ----
    if (Array.isArray(v.occupancy)) {
      v.occupancy = v.occupancy.map((od: any) => ({
        room_no: Number(od.room_no) || 1,
        adult: Number(od.adult) || 1,
        child: Number(od.child) || 0,
        child_age: Array.isArray(od.child_age)
          ? od.child_age.map((a: any) => Number(a) || 1)
          : [],
      }));
    }

    return v;
  }
  private getNextDate(date: Date, days: number = 1): Date {
    const next = new Date(date);
    next.setDate(date.getDate() + days);
    return next;
  }

  private getToday(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  private getDatePlusMonths(date: Date, months: number = 1): Date {
    const d = new Date(date);
    const day = d.getDate();
    d.setMonth(d.getMonth() + months);
    // If month overflowed (e.g., Jan 31 -> Mar 03), snap to last day of previous month
    if (d.getDate() !== day) {
      d.setDate(0);
    }
    return d;
  }

  private createRoomAndGuestFormGroup(): FormGroup {
    const roomFormGroup = this._fb.group(
      {
        roomCount: [1, Validators.required],
        adultCount: [1, [Validators.required, Validators.min(1)]],
        childCount: [0, [Validators.min(0)]],
        child_age: this._fb.array([]),
      },
      {
        validators: [
          this.adultsNotLessThanRoomsValidator,
          this.totalPaxMaxNineValidator,
        ],
      },
    );

    // Ensure adults are never less than rooms, and total pax <= 9
    roomFormGroup.get('roomCount')?.valueChanges.subscribe((roomCount) => {
      const rcRaw = Number(roomCount) || 0;
      const rc = Math.min(Math.max(1, rcRaw), 9);
      if (rc !== rcRaw) {
        roomFormGroup.get('roomCount')?.setValue(rc, { emitEvent: false });
      }
      const adultCtrl = roomFormGroup.get('adultCount');
      const childCtrl = roomFormGroup.get('childCount');
      const ac = Number(adultCtrl?.value) || 0;
      const ch = Number(childCtrl?.value) || 0;
      if (adultCtrl && rc > ac) {
        const cap = Math.max(rc, 1);
        const newAdult = Math.min(cap, 9 - ch + Math.max(0, ch)); // ensure not over 9
        adultCtrl.setValue(newAdult);
        // If bumping adults makes total exceed 9, clamp children down
        const childMax = Math.max(0, 9 - newAdult);
        if (childCtrl && ch > childMax) {
          childCtrl.setValue(childMax);
        }
      }
    });
    roomFormGroup.get('adultCount')?.valueChanges.subscribe((adultCount) => {
      const ac = Number(adultCount) || 0;
      const roomCtrl = roomFormGroup.get('roomCount');
      const rc = Number(roomCtrl?.value) || 0;
      const childCtrl = roomFormGroup.get('childCount');
      const ch = Number(childCtrl?.value) || 0;
      let desired = ac;
      // Keep adults at least rooms
      if (desired < rc) desired = rc;
      // Max adults given children present
      const maxAdults = Math.max(rc, 9 - ch);
      if (desired > maxAdults) desired = maxAdults;
      if (desired !== ac) {
        roomFormGroup
          .get('adultCount')
          ?.setValue(desired, { emitEvent: false });
      }
      // Also clamp children if needed when adults increased
      const childMax = Math.max(0, 9 - desired);
      if (childCtrl && ch > childMax) {
        childCtrl.setValue(childMax, { emitEvent: false });
      }
    });
    roomFormGroup.get('childCount')?.valueChanges.subscribe((childCount) => {
      const chRaw = Number(childCount) || 0;
      const adultCtrl = roomFormGroup.get('adultCount');
      const ac = Number(adultCtrl?.value) || 0;
      const maxChildren = Math.max(0, 9 - ac);
      const ch = Math.min(Math.max(0, chRaw), maxChildren);
      if (ch !== chRaw) {
        roomFormGroup.get('childCount')?.setValue(ch, { emitEvent: false });
      }
      this.setChildAges(ch, roomFormGroup);
    });

    return roomFormGroup;
  }

  createRoomFormGroup(): FormGroup {
    const roomFormGroup = this._fb.group({
      room_no: [1, Validators.required],
      adult: [1, [Validators.required, Validators.min(1)]],
      child: [0, [Validators.min(0)]],
      child_age: this._fb.array([]),
    });

    roomFormGroup.get('child')?.valueChanges.subscribe((childCount) => {
      this.setChildAges(childCount || 0, roomFormGroup);
    });

    return roomFormGroup;
  }

  setChildAges(childCount: number, roomFormGroup: FormGroup): void {
    const childAgeFormArray = roomFormGroup.get('child_age') as FormArray;

    // Clear existing child age controls
    while (childAgeFormArray.length !== 0) {
      childAgeFormArray.removeAt(0);
    }

    // Add new child age controls based on childCount
    for (let i = 0; i < childCount; i++) {
      // Child age must be between 1 and 12 (inclusive)
      childAgeFormArray.push(
        this._fb.control(1, [
          Validators.required,
          Validators.min(1),
          Validators.max(12),
        ]),
      );
    }
  }

  // Dynamic bounds for counters in template
  get adultMin(): number {
    const rc = Number(this.roomAndGuestFormGroup.get('roomCount')?.value) || 1;
    return Math.max(1, rc);
  }
  get adultMax(): number {
    const ch = Number(this.roomAndGuestFormGroup.get('childCount')?.value) || 0;
    return Math.max(this.adultMin, 9 - ch);
  }
  get childMin(): number {
    return 0;
  }
  get childMax(): number {
    const ac = Number(this.roomAndGuestFormGroup.get('adultCount')?.value) || 0;
    return Math.max(0, 9 - ac);
  }

  // Handle date range change from ngx-bootstrap date range picker
  onDateRangeChange(dateRange: (Date | undefined)[] | undefined): void {
    const today = this.getToday();

    // ❌ No date selected
    if (!dateRange || !dateRange[0]) {
      this.hotelForm.patchValue({
        checkin: today,
        checkout: this.getNextDate(today, 1),
      });
      return;
    }

    let checkin = new Date(dateRange[0]);
    let checkout = dateRange[1] ? new Date(dateRange[1]) : null;

    // ✅ Prevent past checkin
    if (checkin < today) {
      checkin = today;
    }

    // ✅ Ensure checkout > checkin
    if (!checkout || checkout <= checkin) {
      checkout = this.getNextDate(checkin, 1);
    }

    this.hotelForm.patchValue({
      checkin,
      checkout,
    });
  }

  getFormattedDate(fieldName: 'checkin' | 'checkout'): string {
    const dateValue = this.hotelForm.get(fieldName)?.value;
    if (dateValue && dateValue instanceof Date) {
      const day = ('0' + dateValue.getDate()).slice(-2);
      const month = ('0' + (dateValue.getMonth() + 1)).slice(-2);
      const year = dateValue.getFullYear();
      return `${day}-${month}-${year}`;
    }
    return '';
  }

  getDateLabel(fieldName: 'checkin' | 'checkout'): string {
    const dateValue = this.hotelForm.get(fieldName)?.value;
    if (dateValue && dateValue instanceof Date && !isNaN(dateValue.getTime())) {
      const days = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      return days[dateValue.getDay()];
    }
    return fieldName === 'checkin'
      ? 'Select check-in date'
      : 'Select check-out date';
  }

  openDateRangePicker(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (window.innerWidth <= 767) {
      document.body.classList.add('datepicker-open');
    }

    if (this.dateRangePicker) {
      this.dateRangePicker.show();
    }

    setTimeout(() => {
      this.addMobileCloseHandler();
    }, 50);

    if (window.innerWidth <= 767) {
      setTimeout(() => {
        this.forceMobileStylesAggressively();
      }, 10);
    }
  }

  private addMobileCloseHandler(): void {
    const datePickerContainer = document.querySelector(
      '.bs-datepicker-container',
    );
    if (datePickerContainer && window.innerWidth <= 767) {
      const overlay = datePickerContainer as HTMLElement;

      this.forceMobileStyles(overlay);

      const handleOverlayClick = (event: Event) => {
        const target = event.target as HTMLElement;

        if (
          target === overlay ||
          target.classList.contains('bs-datepicker-container')
        ) {
          this.closeDatePicker();
          overlay.removeEventListener('click', handleOverlayClick);
          document.removeEventListener('keydown', handleEscapeKey);
        }

        if (target === overlay) {
          const rect = overlay.getBoundingClientRect();
          const clickX = (event as MouseEvent).clientX - rect.left;
          const clickY = (event as MouseEvent).clientY - rect.top;

          if (clickX > rect.width - 70 && clickY < 70) {
            this.closeDatePicker();
            overlay.removeEventListener('click', handleOverlayClick);
            document.removeEventListener('keydown', handleEscapeKey);
          }
        }
      };

      const handleEscapeKey = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.closeDatePicker();
          overlay.removeEventListener('click', handleOverlayClick);
          document.removeEventListener('keydown', handleEscapeKey);
        }
      };

      overlay.addEventListener('click', handleOverlayClick);
      document.addEventListener('keydown', handleEscapeKey);
    }
  }

  private forceMobileStyles(container: HTMLElement): void {
    if (window.innerWidth <= 767) {
      container.style.setProperty('position', 'fixed', 'important');
      container.style.setProperty('top', '0px', 'important');
      container.style.setProperty('left', '0px', 'important');
      container.style.setProperty('right', '0px', 'important');
      container.style.setProperty('bottom', '0px', 'important');
      container.style.setProperty('width', 'max-content', 'important');
      container.style.setProperty('height', 'max-content', 'important');
      container.style.setProperty('transform', 'none', 'important');
      container.style.setProperty('will-change', 'auto', 'important');
      container.style.setProperty('display', 'flex', 'important');
      container.style.setProperty('align-items', 'center', 'important');
      container.style.setProperty('justify-content', 'center', 'important');
      container.style.setProperty('background', 'white', 'important');
      container.style.setProperty('z-index', '9999', 'important');
      container.style.setProperty('box-sizing', 'border-box', 'important');
      container.style.setProperty('margin', '0', 'important');

      const datePicker = container.querySelector(
        '.bs-datepicker',
      ) as HTMLElement;
      if (datePicker) {
        datePicker.style.setProperty('position', 'relative', 'important');
        datePicker.style.setProperty('transform', 'none', 'important');
        datePicker.style.setProperty('top', 'auto', 'important');
        datePicker.style.setProperty('left', 'auto', 'important');
        datePicker.style.setProperty('width', '100%', 'important');
        datePicker.style.setProperty('max-width', '350px', 'important');
        datePicker.style.setProperty('max-height', '85vh', 'important');
      }

      const observer = new MutationObserver(() => {
        if (window.innerWidth <= 767 && container.isConnected) {
          this.forceMobileStyles(container);
        }
      });

      observer.observe(container, {
        attributes: true,
        attributeFilter: ['style'],
      });

      setTimeout(() => {
        observer.disconnect();
      }, 10000);
    }
  }

  private forceMobileStylesAggressively(): void {
    if (window.innerWidth <= 767) {
      const containers = document.querySelectorAll('.bs-datepicker-container');
      containers.forEach((container) => {
        const element = container as HTMLElement;

        element.style.removeProperty('transform');
        element.style.removeProperty('will-change');

        const mobileStyles = `
          position: fixed !important;
          top: 0px !important;
          left: 0px !important;
          right: 0px !important;
          bottom: 0px !important;
          width: max-content !important;
          height: max-content !important;
          transform: none !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          z-index: 9999 !important;
          box-sizing: border-box !important;
          margin: 0 !important;
        `;

        element.setAttribute('style', mobileStyles);

        const datePicker = element.querySelector(
          '.bs-datepicker',
        ) as HTMLElement;
        if (datePicker) {
          const innerStyles = `
            position: relative !important;
            transform: none !important;
            top: auto !important;
            left: auto !important;
            width: 100% !important;
            max-width: 350px !important;
            max-height: 85vh !important;
            background: #fff !important;
            border-radius: 8px !important;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.5) !important;
            overflow: hidden !important;
            border: none !important;
            margin: 0 !important;
          `;
          datePicker.setAttribute(
            'style',
            datePicker.getAttribute('style') + '; ' + innerStyles,
          );
        }
      });

      let attempts = 0;
      const intervalId = setInterval(() => {
        attempts++;
        if (attempts > 20) {
          clearInterval(intervalId);
          return;
        }

        const containers = document.querySelectorAll(
          '.bs-datepicker-container',
        );
        containers.forEach((container) => {
          const element = container as HTMLElement;
          if (element.style.transform && element.style.transform !== 'none') {
            this.forceMobileStylesAggressively();
          }
        });
      }, 100);
    }
  }

  closeDatePicker(): void {
    document.body.classList.remove('datepicker-open');

    if (this.dateRangePicker) {
      this.dateRangePicker.hide();
    }
  }

  onCitySection($event: City) {
    this.hotelForm.patchValue({
      city_name: $event.cityName,
      country_name: $event.countryName,
    });
  }

  onSearch() {
    this.getLocalStorageValues();
    if (this.hotelForm.invalid) return;

    const formValue = JSON.parse(JSON.stringify(this.hotelForm.value));

    formValue.checkin = this.formatLocalDate(formValue.checkin);
    formValue.checkout = this.formatLocalDate(formValue.checkout);

    const d = formValue.roomAndGuest;
    const occupancy = this.searchBarService.generateOccupancy(
      d.roomCount,
      d.adultCount,
      d.childCount,
      this.childAgeFormArray().value,
    );

    this.occupancyFormArray.clear();
    occupancy.forEach((room: any) => {
      const roomGroup = this.createRoomFormGroup();
      roomGroup.patchValue(room);
      this.occupancyFormArray.push(roomGroup);
    });

    formValue.occupancy = this.occupancyFormArray.value;

    const finalPayload = {
      ...formValue,
      checkin: this.formatLocalDate(formValue.checkin),
      checkout: this.formatLocalDate(formValue.checkout),
    };

    console.log('FINAL PAYLOAD SENT:', finalPayload);

    this.searchBarService.changeHotelSearchRequest(
      finalPayload as HotelSearchRequest,
    );

    const queryString = this.searchBarService.toQueryString(finalPayload);
    this.search.emit(queryString);
  }

  private formatLocalDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

  get totalGuestCount(): number {
    const guestCount = this.hotelForm.get('roomAndGuest')?.value;
    return guestCount
      ? (parseInt(guestCount.adultCount) || 0) +
          (parseInt(guestCount.childCount) || 0)
      : 0;
  }

  get guestDesc(): string {
    const guestCount = this.hotelForm.get('roomAndGuest')?.value;
    const roomCount = this.hotelForm.get('roomAndGuest')?.value.roomCount;
    if (!guestCount) return '';
    const adultSuffix = guestCount.adultCount > 1 ? 's' : '';
    const childSuffix = guestCount.childCount > 1 ? 'ren' : '';
    const roomSuffix = roomCount > 1 ? 's' : '';
    const adults =
      guestCount.adultCount > 0
        ? `${guestCount.adultCount} Adult${adultSuffix}`
        : '';
    const children =
      guestCount.childCount > 0
        ? `${guestCount.childCount} Child${childSuffix}`
        : '';
    const rooms = roomCount > 0 ? `${roomCount} Room${roomSuffix}` : '';
    return [adults, children, rooms].filter(Boolean).join(', ');
  }

  get occupancyFormArray(): FormArray {
    return this.hotelForm.get('occupancy') as FormArray;
  }

  get roomAndGuestFormGroup(): FormGroup {
    return this.hotelForm.get('roomAndGuest') as FormGroup;
  }

  childAgeFormArray(): FormArray {
    const f = this.roomAndGuestFormGroup.get('child_age') as FormArray;
    return f;
  }

  /**
   * Custom validator for checkin/checkout fields
   * - Both dates must not be in the past
   * - Dates must be different
   */
  checkinCheckoutValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const form = control as FormGroup;
    const checkin = form.get('checkin')?.value;
    const checkout = form.get('checkout')?.value;

    if (checkin && checkout) {
      const checkinDate = new Date(checkin);
      const checkoutDate = new Date(checkout);

      if (checkoutDate <= checkinDate) {
        return { invalidDateRange: true };
      }
    }

    return null;
  };

  getCheckoutMinDate(): Date {
    const checkin = this.hotelForm.get('checkin')?.value;
    if (!checkin) return new Date();
    const checkinDate = new Date(checkin);
    checkinDate.setDate(checkinDate.getDate() + 1); // Checkout must be at least one day after checkin
    return checkinDate;
  }

  /**
   * Validator: adults must be at least the number of rooms
   */
  adultsNotLessThanRoomsValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const fg = group as FormGroup;
    const roomCount = Number(fg.get('roomCount')?.value) || 0;
    const adultCount = Number(fg.get('adultCount')?.value) || 0;
    const errors: Record<string, boolean> = {};
    if (adultCount < roomCount) {
      errors['adultsLessThanRooms'] = true;
    }
    return Object.keys(errors).length ? errors : null;
  };

  /**
   * Validator: adults + children must be <= 9
   */
  totalPaxMaxNineValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const fg = group as FormGroup;
    const adultCount = Number(fg.get('adultCount')?.value) || 0;
    const childCount = Number(fg.get('childCount')?.value) || 0;
    return adultCount + childCount > 9 ? { totalPaxExceedsNine: true } : null;
  };

  private get isAllowedHotelSearchUrl(): boolean {
    const url = this.router.url.replace(/^\//, ''); // Remove leading slash if present
    return this.allowedHotelSearchURLs.some((allowed) => url.includes(allowed));
  }

  ngOnDestroy(): void {
    document.body.classList.remove('datepicker-open');
    this.unlockBodyScroll();
    this._autoSaveSub?.unsubscribe();
  }
}
